/**
 * =============================================================================
 * Componente: ReferralMaterialFormModal
 * Versão: 1.0.0 (Fase 5 — Bloco 5D, item 5.10)
 *
 * Descrição:
 *   Modal de criação/edição de material de divulgação (admin).
 *   Suporta tipos: image, video, banner, copy, document.
 *   - Materiais de "copy" usam apenas `text_content`.
 *   - Demais tipos podem fazer upload no bucket `affiliate` (subpasta
 *     `materials/<tenantId>/...`) ou informar uma URL externa.
 * =============================================================================
 */
import { useEffect, useRef, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, Upload, ExternalLink, ImageIcon, Plus, Check } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { useReferralPrograms } from '@/hooks/useReferralPrograms';
import {
  useReferralMaterialMutations,
  uploadMaterialFile,
  saveMaterialPrograms,
  type AffiliateMaterial,
} from '@/hooks/useReferralMaterials';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
// v1.2.0 — Reaproveita o seletor unificado de URL/Galeria já usado em
// ReferralCampaignFormModal, garantindo consistência de UX no módulo.
import { MediaUrlInput } from '@/components/ui/media-url-input';
// v1.3.0 (2026-05-02) — Migrado para FormModalShell compartilhado
// (mesmo padrão premium dos modais de Tarefa, Campanha e Programa).
import { FormModalShell } from '@/components/shared/FormModalShell';
// v1.5.0 (2026-08-17) — Descrição passa a usar o editor rico (formatação +
// emojis), o mesmo componente adotado em tarefas, negócios e anotações.
// O HTML é sanitizado antes de salvar; a busca/compartilhamento usam texto puro.
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import { sanitizeHtml, stripHtml } from '@/lib/sanitize';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  material?: AffiliateMaterial | null;
  /**
   * v1.1.0: pré-seleciona o programa quando aberto a partir do detalhe do
   * programa. Em modo edição, o valor original do material prevalece.
   */
  defaultProgramId?: string;
}

interface FormState {
  title: string;
  description: string;
  material_type: 'image' | 'video' | 'banner' | 'copy' | 'document';
  /** v1.4.0 — vínculo N:N: lista de programas selecionados. */
  program_ids: string[];
  text_content: string;
  file_url: string;
  storage_path: string | null;
  file_size_bytes: number | null;
  mime_type: string | null;
  sort_order: string;
  is_active: boolean;
}

const empty: FormState = {
  title: '',
  description: '',
  material_type: 'image',
  program_ids: [],
  text_content: '',
  file_url: '',
  storage_path: null,
  file_size_bytes: null,
  mime_type: null,
  sort_order: '0',
  is_active: true,
};

export function ReferralMaterialFormModal({
  open,
  onOpenChange,
  material,
  defaultProgramId,
}: Props) {
  const { toast } = useToast();
  const { currentTenant } = useAuth();
  const { data: programs = [] } = useReferralPrograms();
  const { createMutation, updateMutation } = useReferralMaterialMutations();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [form, setForm] = useState<FormState>(empty);
  const [uploading, setUploading] = useState(false);
  const [savingPrograms, setSavingPrograms] = useState(false);

  const isEdit = !!material?.id;
  const saving =
    createMutation.isPending || updateMutation.isPending || savingPrograms;
  const isCopy = form.material_type === 'copy';

  useEffect(() => {
    if (!open) return;
    if (material) {
      setForm({
        title: material.title ?? '',
        description: material.description ?? '',
        material_type:
          (material.material_type as FormState['material_type']) ?? 'image',
        program_ids:
          material.program_ids && material.program_ids.length > 0
            ? material.program_ids
            : material.program_id
              ? [material.program_id]
              : [],
        text_content: material.text_content ?? '',
        file_url: material.file_url ?? '',
        storage_path: material.storage_path ?? null,
        file_size_bytes: material.file_size_bytes ?? null,
        mime_type: material.mime_type ?? null,
        sort_order: String(material.sort_order ?? 0),
        is_active: !!material.is_active,
      });
    } else {
      // v1.1.0: aplica defaultProgramId no caso de novo material
      setForm({
        ...empty,
        program_ids: defaultProgramId ? [defaultProgramId] : [],
      });
    }
  }, [open, material]);

  /** Marca/desmarca um programa na seleção múltipla. */
  const toggleProgram = (id: string) => {
    setForm((p) => ({
      ...p,
      program_ids: p.program_ids.includes(id)
        ? p.program_ids.filter((x) => x !== id)
        : [...p.program_ids, id],
    }));
  };


  const handlePickFile = () => fileInputRef.current?.click();

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !currentTenant?.id) return;
    setUploading(true);
    try {
      const result = await uploadMaterialFile({
        tenantId: currentTenant.id,
        file,
      });
      setForm((p) => ({
        ...p,
        file_url: result.file_url,
        storage_path: result.storage_path,
        file_size_bytes: result.file_size_bytes,
        mime_type: result.mime_type,
      }));
      toast({ title: 'Arquivo enviado.' });
    } catch (err: any) {
      // Traduz erro de RLS/permissão do storage em mensagem compreensível
      const raw = String(err?.message ?? '');
      const isPermission =
        /row-level security|violates row-level|permission|unauthorized/i.test(raw);
      toast({
        title: 'Erro no upload',
        description: isPermission
          ? 'Você não tem permissão para enviar materiais neste tenant.'
          : raw || 'Tente novamente.',
        variant: 'destructive',
      });
    } finally {

      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async () => {
    if (!form.title.trim()) {
      toast({
        title: 'Título obrigatório',
        variant: 'destructive',
      });
      return;
    }
    if (isCopy && !form.text_content.trim()) {
      toast({
        title: 'Texto obrigatório para materiais do tipo Copy',
        variant: 'destructive',
      });
      return;
    }
    if (!isCopy && !form.file_url.trim()) {
      toast({
        title: 'Arquivo obrigatório',
        description: 'Faça upload ou informe uma URL.',
        variant: 'destructive',
      });
      return;
    }

    const payload = {
      title: form.title.trim(),
      // v1.5.0 — grava HTML sanitizado; vazio (só tags) vira NULL.
      description: stripHtml(form.description).trim()
        ? sanitizeHtml(form.description)
        : null,
      material_type: form.material_type,
      // Mantém compatibilidade com a coluna legada (primeiro programa).
      program_id: form.program_ids[0] ?? null,
      text_content: isCopy ? form.text_content.trim() : null,
      file_url: isCopy ? null : form.file_url.trim() || null,
      storage_path: isCopy ? null : form.storage_path,
      file_size_bytes: isCopy ? null : form.file_size_bytes,
      mime_type: isCopy ? null : form.mime_type,
      sort_order: Number(form.sort_order) || 0,
      is_active: form.is_active,
    };

    try {
      let materialId = material?.id ?? null;
      if (isEdit && material) {
        await updateMutation.mutateAsync({ id: material.id, patch: payload });
      } else {
        const created = await createMutation.mutateAsync(payload as any);
        materialId = created?.id ?? null;
      }

      // v1.4.0 — grava os vínculos N:N com os programas selecionados.
      if (materialId) {
        setSavingPrograms(true);
        try {
          await saveMaterialPrograms({
            materialId,
            programIds: form.program_ids,
          });
        } finally {
          setSavingPrograms(false);
        }
      }

      toast({ title: isEdit ? 'Material atualizado.' : 'Material criado.' });
      onOpenChange(false);
    } catch (err: any) {
      toast({
        title: 'Erro ao salvar material',
        description: err?.message ?? 'Tente novamente.',
        variant: 'destructive',
      });
    }
  };


  return (
    <FormModalShell
      open={open}
      onOpenChange={onOpenChange}
      icon={ImageIcon}
      title={isEdit ? 'Editar material' : 'Novo material'}
      description="Banners, vídeos, copies e documentos disponíveis aos afiliados."
      onSubmit={handleSubmit}
      isSubmitting={saving}
      submitLabel={isEdit ? 'Salvar alterações' : 'Criar material'}
      submitIcon={isEdit ? Check : Plus}
      maxWidth="max-w-3xl"
    >
      <div className="grid gap-4">
        <div className="grid gap-2">
          <Label htmlFor="title">Título *</Label>
          <Input
            id="title"
            value={form.title}
            onChange={(e) =>
              setForm((p) => ({ ...p, title: e.target.value }))
            }
            placeholder="Ex.: Banner principal 1080x1080"
          />
        </div>

        <div className="grid gap-2">
          <Label htmlFor="desc">Descrição</Label>
          {/* v1.5.0 — editor rico (formatação + emojis), mesmo componente
              usado em tarefas/negócios/anotações. */}
          <RichTextEditor
            content={form.description}
            onChange={(html) => setForm((p) => ({ ...p, description: html }))}
            placeholder="Resumo curto exibido no card."
            minHeight="90px"
            maxHeight="220px"
            fontSize="sm"
          />
        </div>

        <div className="grid gap-3 sm:grid-cols-3">
          <div className="grid gap-2">
            <Label>Tipo *</Label>
            <Select
              value={form.material_type}
              onValueChange={(v: FormState['material_type']) =>
                setForm((p) => ({ ...p, material_type: v }))
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="image">Imagem</SelectItem>
                <SelectItem value="banner">Banner</SelectItem>
                <SelectItem value="video">Vídeo</SelectItem>
                <SelectItem value="document">Documento</SelectItem>
                <SelectItem value="copy">Texto / Copy</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {/* v1.4.0 — seleção múltipla de programas (N:N). Sem seleção =
              material disponível para todos os programas. */}
          <div className="grid gap-2 sm:col-span-2">
            <Label>Programas (opcional)</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  className="justify-start font-normal h-10"
                >
                  {form.program_ids.length === 0 ? (
                    <span className="text-muted-foreground">
                      Todos os programas
                    </span>
                  ) : (
                    <span className="flex flex-wrap gap-1">
                      {programs
                        .filter((p) => form.program_ids.includes(p.id))
                        .map((p) => (
                          <Badge key={p.id} variant="secondary">
                            {p.name}
                          </Badge>
                        ))}
                    </span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[--radix-popover-trigger-width] p-2">
                <div className="max-h-64 overflow-auto space-y-1">
                  {programs.length === 0 && (
                    <p className="p-2 text-sm text-muted-foreground">
                      Nenhum programa cadastrado.
                    </p>
                  )}
                  {programs.map((p) => (
                    <label
                      key={p.id}
                      className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-muted cursor-pointer"
                    >
                      <Checkbox
                        checked={form.program_ids.includes(p.id)}
                        onCheckedChange={() => toggleProgram(p.id)}
                      />
                      <span className="truncate">{p.name}</span>
                    </label>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
            <p className="text-xs text-muted-foreground">
              Deixe vazio para disponibilizar em todos os programas.
            </p>
          </div>

        </div>

        {isCopy ? (
          <div className="grid gap-2">
            <Label htmlFor="copy">Texto / Copy *</Label>
            <Textarea
              id="copy"
              rows={6}
              value={form.text_content}
              onChange={(e) =>
                setForm((p) => ({ ...p, text_content: e.target.value }))
              }
              placeholder="Texto pronto que o afiliado poderá copiar..."
            />
          </div>
        ) : (
          <div className="space-y-3 rounded-lg border p-3">
            <div className="flex items-center justify-between gap-2">
              <div>
                <Label>Arquivo</Label>
                <p className="text-xs text-muted-foreground">
                  Faça upload ou informe uma URL externa.
                </p>
              </div>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={handlePickFile}
                disabled={uploading}
              >
                {uploading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Upload className="h-4 w-4 mr-2" />
                )}
                {uploading ? 'Enviando...' : 'Enviar arquivo'}
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                onChange={handleFileChange}
                accept="image/*,video/*,application/pdf"
              />
            </div>

            <div className="grid gap-2">
              <Label>URL do arquivo</Label>
              {/* v1.2.0 — Substitui o Input puro pelo MediaUrlInput, que
                  combina campo manual + botão de galeria de mídia do tenant
                  (mesmo padrão de ReferralCampaignFormModal). */}
              <MediaUrlInput
                value={form.file_url || null}
                onChange={(url) =>
                  setForm((p) => ({
                    ...p,
                    file_url: url ?? '',
                    // se trocou via galeria/manual, descarta storage_path
                    // anterior para não excluir o arquivo do upload local
                    storage_path: null,
                  }))
                }
                placeholder="https://… ou escolha da galeria"
                allowedTypes={['image', 'video', 'document']}
                showPreview
              />
            </div>

            {form.file_url && (
              <a
                href={form.file_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-primary inline-flex items-center gap-1 hover:underline"
              >
                <ExternalLink className="h-3 w-3" />
                Pré-visualizar arquivo
              </a>
            )}
          </div>
        )}

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="grid gap-2">
            <Label htmlFor="sort">Ordem</Label>
            <Input
              id="sort"
              type="number"
              value={form.sort_order}
              onChange={(e) =>
                setForm((p) => ({ ...p, sort_order: e.target.value }))
              }
            />
          </div>
          <div className="flex items-center justify-between rounded-lg border p-3">
            <div>
              <Label>Material ativo</Label>
              <p className="text-xs text-muted-foreground">
                Quando inativo, não aparece para os afiliados.
              </p>
            </div>
            <Switch
              checked={form.is_active}
              onCheckedChange={(v) =>
                setForm((p) => ({ ...p, is_active: v }))
              }
            />
          </div>
        </div>
      </div>
    </FormModalShell>
  );
}
